/**
 * CJAS — the admin shell.  Jira CA-31.
 *
 * Two or three people use this, between other jobs, often on a phone. So the
 * navigation is short, the labels are Norwegian, and nothing is hidden behind
 * a settings menu that nobody will find.
 *
 * The rule underneath: a menu item a person cannot use is not shown to them.
 * Not greyed out — absent. A greyed-out "Godkjenn push" teaches staff that the
 * system is arbitrary; an absent one teaches them nothing at all, which is
 * correct, because it is not their job.
 */

const access = require('./access');

/**
 * The panel's navigation — the ONE list.
 *
 * REWRITTEN 2026-09-10 (fault 58). This was a second navigation that nobody
 * rendered: different ids ('prayer', 'requests', 'push', 'import'), different
 * labels ("Oversikt", "Kunngjøringer", "Henvendelser"), no Bannere, no
 * Tilganger, no Aktivitetslogg — while the panel people actually use built its
 * own list in `admin-live.js` (VIEW_PERMISSION) and `admin.html`. Two lists of
 * who may see what; the one with tests was the one nobody ran. Now this is the
 * list, and `admin-shell.test.js` reads both panel files and fails if either
 * disagrees with it — ids, permissions, labels, order, and which items badge.
 *
 * `parts` gates a card INSIDE a view: an editor may link members but not
 * import the register, so the upload box must be absent for them rather than
 * a control that answers 403 (core rule 20).
 */
/**
 * The groups the menu is drawn in.  Daniyal, 2026-09-20.
 *
 * Twelve items in one undivided column is a list you read from the top every
 * time, because nothing tells you where to start looking. The groups say what
 * KIND of job each item is, so somebody who came to fix a maghrib time and
 * somebody who came to switch a partner shop off are looking in two different
 * places rather than the same twelve.
 *
 * `partners` moved up next to `members` so the groups are contiguous: it is
 * about what a membership is worth, not about running the panel. Order is
 * still core's, and the panel's VIEWS and ICON tables are checked against it.
 *
 * A group is a heading, never a collapsing section. Two or three people use
 * this panel between other jobs, and a menu that hides half of itself is a
 * menu they have to learn.
 */
const GROUPS = [
  { id: 'start',   label_no: null },
  // MKK adoption Batch 1: the week's own work — tasks, checklists, the annual planner. Right after the dashboard: it is
  // what a person opens the panel FOR on most days, and on a phone it is most of what a volunteer sees.
  { id: 'work',    label_no: 'Drift' },
  { id: 'content', label_no: 'Innhold' },
  { id: 'venue',   label_no: 'Skjermer i bygget' },
  { id: 'people',  label_no: 'Folk' },
  { id: 'admin',   label_no: 'Administrasjon' },
];

const NAV = [
  { id: 'dash',    label_no: 'Oversikt',       permission: null, route: '/', group: 'start' },
  // Each needs its permission AND its capability (not entitled for any mosque): absent until the mosque has it.
  // `alsoFor` (hardening, 2026-10-02): the manager of the list reaches it without work.assigned — the API always allowed it.
  { id: 'work',    label_no: 'Oppgaver',       permission: 'work.assigned', alsoFor: ['tasks.manage'], capability: 'tasks', route: '/oppgaver', group: 'work' },
  { id: 'checklists', label_no: 'Sjekklister', permission: 'work.assigned', alsoFor: ['checklists.manage'], capability: 'checklists', route: '/sjekklister', group: 'work' },
  { id: 'planner', label_no: 'Årshjul',        permission: 'planner.read', capability: 'planner', route: '/arshjul', group: 'work' },
  // MKK module 13 «Kontakter» (B1, 2026-10-24): the mosque's directory of the people it works with. contacts.read opens it
  // (contacts.manage alone does too); absent until the mosque has the capability.
  { id: 'contacts', label_no: 'Kontakter',     permission: 'contacts.read', alsoFor: ['contacts.manage'], capability: 'contacts_directory', route: '/kontakter', group: 'work' },
  // MKK module 23 «Utlegg» (B2, 2026-10-24): my own claims (expenses.submit, every staff role) and, for an admin, the claims
  // to approve and pay. Absent until the mosque has the capability.
  { id: 'expenses', label_no: 'Utlegg',        permission: 'expenses.submit', alsoFor: ['expenses.approve', 'expenses.pay'], capability: 'expenses', route: '/utlegg', group: 'work' },
  // MKK module 15 «Intern info» + «Viktige dokumenter» (C1, 2026-10-25): the mosque's own archive. documents.read opens it
  // (documents.manage alone does too); absent until the mosque has the capability.
  { id: 'documents', label_no: 'Dokumenter',  permission: 'documents.read', alsoFor: ['documents.manage'], capability: 'documents', route: '/dokumenter', group: 'work' },
  // MKK module 18 «Styremøte» (C2, 2026-10-25): the board's meetings, decisions and minutes. board.read opens it
  // (board.manage alone does too); absent until the mosque has the capability.
  { id: 'board', label_no: 'Styremøter',      permission: 'board.read', alsoFor: ['board.manage'], capability: 'board_meetings', route: '/styremoter', group: 'work' },
  // MKK module 19 «Avstemninger» (C3, 2026-10-25): proposals and votes in the board. voting.vote opens it (the votes one is
  // eligible in); voting.manage alone does too. Absent until the mosque has the capability.
  { id: 'voting', label_no: 'Avstemninger',   permission: 'voting.vote', alsoFor: ['voting.manage'], capability: 'board_voting', route: '/avstemninger', group: 'work' },
  // `alsoNeeds` (Batch 1 remediation): the screen's first reads (times, timetable runway, Hijri; the notice list and the
  // push audience) need operations.read, so the item is shown only with both — never as a page that opens on a 403.
  { id: 'times',   label_no: 'Bønnetider',     permission: 'prayer.write', alsoNeeds: ['operations.read'], route: '/bonnetider', group: 'content' },
  // The Ramadan module (owner 2026-10-31): imsak, taraweeh, the notes, and the iftar/sehri sign-up lists — right under the
  // prayer times, visible all year («Neste ramadan starter ca. …»). The prayer-times door; every mosque has prayer_times.
  { id: 'ramadan', label_no: 'Ramadan',        permission: 'prayer.write', capability: 'prayer_times', route: '/ramadan', group: 'content' },
  // The khutbah plan (MKK adoption A1, 2026-10-24): beside the prayer times, behind the `khutbah_plan` capability.
  { id: 'khutbah', label_no: 'Khutbah',        permission: 'khutbah.manage', capability: 'khutbah_plan', route: '/khutbah', group: 'content' },
  // Tasbih-innsamling (owner 2026-11-02): collections members send their tasbih count to from the app. Behind the `tasbih`
  // capability (not entitled anywhere) and tasbih.manage (admin tier and the imam).
  { id: 'tasbih',  label_no: 'Tasbih',         permission: 'tasbih.manage', capability: 'tasbih', route: '/tasbih', group: 'content' },
  { id: 'events',  label_no: 'Arrangementer',  permission: 'events.write', route: '/program', group: 'content' },
  // MKK adoption Batch 2A: staff event operations — registration lists, the waiting list, check-in. Behind the
  // `event_registration` capability (not entitled anywhere). Counts for events.write; names and actions need the
  // admin-tier permissions, which also open the view for an editor granted one by name (MKK_ADOPTION_BATCH_2.md §11.5).
  { id: 'registration', label_no: 'Påmelding',  permission: 'events.write', alsoFor: ['events.registrations.manage', 'events.checkin', 'events.attendees.read'],
    capability: 'event_registration', route: '/pamelding', group: 'content' },
  { id: 'notices', label_no: 'Varsler',  permission: 'notices.write', alsoNeeds: ['operations.read'], route: '/varsler', group: 'content' },
  // «Utskrifter» (MKK #34, 2026-10-29): the Ramadan calendar and the month's prayer timetable to print or download as a
  // PDF. A read view over the timetable, so the prayer-times page's own read door (operations.read + prayer.read); no
  // capability — every mosque has a timetable.
  { id: 'printables', label_no: 'Utskrifter',  permission: 'operations.read', alsoNeeds: ['prayer.read'], route: '/utskrifter', group: 'content' },
  // WhatsApp, phase 1 (MKK module 37, 2026-10-24): the mosque's groups and channels, a message composed from a notice, an
  // event or a banner, copied or opened in WhatsApp, and the log. whatsapp.send opens it (whatsapp.manage alone does too);
  // absent until the mosque switches the capability on.
  { id: 'whatsapp', label_no: 'WhatsApp',      permission: 'whatsapp.send', alsoFor: ['whatsapp.manage'], capability: 'whatsapp', route: '/whatsapp', group: 'content' },
  { id: 'trans',   label_no: 'Oversettelser',  permission: 'translations.review', route: '/oversettelser', group: 'content' },
  { id: 'screens', label_no: 'Skjermer',       permission: 'screens.manage', route: '/skjermer', group: 'venue' },
  // Banner hardening (2026-10-03): also needs the mosque's `banners` capability — every banner route answers 404 without it.
  { id: 'banners', label_no: 'Bannere',        permission: 'screens.manage', capability: 'banners', route: '/bannere', group: 'venue' },
  { id: 'inbox',   label_no: 'Henvendelser',   permission: 'requests.read', route: '/foresporsler', group: 'people' },
  // One item for the whole membership area (2026-10-15), with tabs inside (MEMBER_TABS). «Medlemsbekreftelse» was its
  // own item; it is now «Samtykke» under «Import og brev», and its old address leads there (ALIASES).
  { id: 'members', label_no: 'Medlemmer',      permission: 'members.link', route: '/medlemmer', parts: { import: 'members.import' }, group: 'people' },
  { id: 'partners', label_no: 'Medlemsfordeler', permission: 'partners.manage', route: '/partnerbutikker', group: 'people' },
  // OA-002: the mosque's Vipps gifts and their payment state (admin; no giver named).
  { id: 'donations', label_no: 'Gaver', permission: 'donations.read', route: '/gaver', group: 'people' },
  // MKK module 21 «Innsamling» (2026-10-29): campaigns with a goal and their progress; the board reads, the treasurer writes.
  { id: 'campaigns', label_no: 'Innsamlinger', permission: 'campaigns.read', alsoFor: ['campaigns.manage'], capability: 'campaigns', route: '/innsamlinger', group: 'people' },
  // Store foundation (staging only): products and draft orders. Also needs the mosque's `store` CAPABILITY — off and
  // not entitled anywhere — so the item is absent unless the mosque has the store (capabilityAllows).
  { id: 'store', label_no: 'Butikk', permission: 'store.manage', capability: 'store', route: '/butikk', group: 'people' },
  // MKK module 22 «Kontantgaver» (R2, 2026-10-29): cash gifts recorded by one and confirmed by another, with receipts.
  // cash_gifts.record opens it (cash_gifts.confirm alone does too); absent until the mosque has the capability.
  // Budsjett (money-pro G1, 2026-10-31): budget against actual for the year. Admin and the treasurer; absent until the
  // mosque switches the capability on.
  { id: 'budget', label_no: 'Budsjett', permission: 'finance.budget', capability: 'budget', route: '/budsjett', group: 'people' },
  { id: 'cashGifts', label_no: 'Kontantgaver', permission: 'cash_gifts.record', alsoFor: ['cash_gifts.confirm'], capability: 'cash_gifts', route: '/kontantgaver', group: 'people' },
  { id: 'access',  label_no: 'Tilganger',      permission: 'staff.manage', route: '/tilganger', group: 'admin' },
  { id: 'audit',   label_no: 'Aktivitetslogg', permission: 'audit.read', route: '/aktivitetslogg', group: 'admin' },
  // OA-013: what the mosque HAS (capabilities), as opposed to who may do what (Tilganger).
  // PM gaps B3 (2026-11-01): an admin (settings.write) READS it too — the Ramadan and board pages send admins here — and
  // switching stays the super admin's (capabilities.manage), on the server as in the view.
  { id: 'features', label_no: 'Funksjoner', permission: 'capabilities.manage', alsoFor: ['settings.write'], route: '/funksjoner', group: 'admin' },
  // OA-012: how long the mosque keeps each category of personal data (read: admin; change: super admin).
  { id: 'retention', label_no: 'Oppbevaring', permission: 'retention.read', route: '/oppbevaring', group: 'admin' },
  // The mosque's own privacy page (2026-10-14): upload, preview, publish, put back; Min Masjid approves it (admin tier).
  { id: 'privacy', label_no: 'Personvern', permission: 'privacy.manage', route: '/personvern', group: 'admin' },
  // The mosque's own website (2026-10-20): the WordPress plugin, its API address and the website key (admin tier).
  { id: 'website', label_no: 'Nettside', permission: 'settings.write', route: '/nettside', group: 'admin' },
  // The mosque's «Kontakt» page in the member app (2026-10-30): address, phone, e-mail, website and the people to contact,
  // with designation (admin tier, as Nettside: what the mosque says about itself to everybody). No capability: core.
  { id: 'contactPage', label_no: 'Kontaktinfo', permission: 'settings.write', route: '/kontaktinfo', group: 'admin' },
];

/**
 * The tabs inside «Medlemmer» (2026-10-15). Fifteen cards on one page, and five more on «Medlemsbekreftelse», became
 * four tabs: the work waiting for somebody, the register (with one page per person), the import and the letters, and
 * the settings nobody opens weekly. A tab is shown when the person holds ANY of its permissions; each card inside
 * keeps its own gate, so a tab never shows a card that answers 403 (core rule 20). A person's page
 * (/medlemmer/person/<id>) belongs to «Register».
 */
const MEMBER_TABS = [
  { id: 'inbox',    label_no: 'Til behandling', route: '/medlemmer',
    anyOf: ['membership.decide', 'membership.declaration.record', 'membership.declaration.confirm', 'membership.withdrawal.record'] },
  { id: 'register', label_no: 'Register',       route: '/medlemmer/register', anyOf: ['members.link'] },
  // Medlemsbevis (the mosque's PDF) are listed here too, so an approver without the import sees this tab with that card.
  { id: 'import',   label_no: 'Import og brev', route: '/medlemmer/import',   anyOf: ['members.import', 'membership.decide'] },
  { id: 'setup',    label_no: 'Oppsett',        route: '/medlemmer/oppsett',  anyOf: ['members.link', 'staff.app_account.declare', 'audit.read'] },
];
function memberTabsFor(can) { return MEMBER_TABS.filter((t) => t.anyOf.some(can)); }

/**
 * Where an address under «Medlemmer» leads: the tab, and a person's id. `route` is the decoded hash path. Unknown
 * sub-addresses fall back to the first tab the person may open — never a blank page.
 */
function memberRoute(route, can) {
  const tabs = memberTabsFor(can);
  const rest = String(route || '').replace(/^\/medlemmer\/?/, '').split('/').filter(Boolean);
  if (rest[0] === 'person' && rest[1] && tabs.some((t) => t.id === 'register')) return { tab: 'register', personId: rest[1] };
  const hit = tabs.find((t) => t.route === '/medlemmer' + (rest[0] ? '/' + rest[0] : ''));
  return { tab: hit ? hit.id : (tabs[0] ? tabs[0].id : null), personId: null };
}

/**
 * The menu as staff see it: seven jobs, not twenty-two pages.  Masjid UX redesign (2026-10-23).
 *
 * The grouped list of twenty-two items was a list people read from the top every time. A SECTION is a job —
 * «Søknader», «Medlemmer», «Gaver», … — and its pages become tabs at the top of the page. NAV above stays the one list
 * of pages, routes and permissions; a section only says which pages belong to which job. A page is shown when its NAV
 * item is (permission + capability) and, for a «Medlemmer» tab, when MEMBER_TABS says so. A section with no page this
 * person may open is absent (core rule 20). Routes are unchanged, so every bookmark still works.
 *
 * `label_no` on a page overrides the NAV label inside the section's tab bar.
 */
const SECTIONS = [
  { id: 'overview',     label_no: 'Oversikt',                 pages: [{ nav: 'dash' }] },
  { id: 'applications', label_no: 'Søknader',                 pages: [{ nav: 'members', tab: 'inbox', label_no: 'Til behandling' }] },
  { id: 'members',      label_no: 'Medlemmer',                pages: [{ nav: 'members', tab: 'register' }, { nav: 'members', tab: 'import' },
                                                                     { nav: 'partners', label_no: 'Medlemsfordeler' }, { nav: 'members', tab: 'setup' }] },
  { id: 'donations',    label_no: 'Gaver',                    pages: [{ nav: 'donations' }, { nav: 'campaigns' }, { nav: 'store' }, { nav: 'cashGifts' }, { nav: 'budget' }] },
  { id: 'content',      label_no: 'Arrangementer og innhold', pages: [{ nav: 'events' }, { nav: 'registration' }, { nav: 'notices' }, { nav: 'times' }, { nav: 'ramadan' }, { nav: 'printables' },
                                                                     { nav: 'khutbah' }, { nav: 'tasbih' }, { nav: 'banners' }, { nav: 'screens' }, { nav: 'trans' }, { nav: 'whatsapp' }] },
  { id: 'tasks',        label_no: 'Oppgaver',                 pages: [{ nav: 'inbox', label_no: 'Henvendelser' }, { nav: 'work' }, { nav: 'checklists' }, { nav: 'planner' },
                                                                     { nav: 'contacts' }, { nav: 'expenses' }, { nav: 'documents' }, { nav: 'board' }, { nav: 'voting' }] },
  { id: 'settings',     label_no: 'Innstillinger',            pages: [{ nav: 'access' }, { nav: 'features' }, { nav: 'website' }, { nav: 'contactPage' }, { nav: 'privacy' },
                                                                     { nav: 'retention' }, { nav: 'audit' }] },
];

/** A page's address: a «Medlemmer» tab's own route, otherwise the NAV item's. */
function pageRoute(page) {
  if (page.tab) return MEMBER_TABS.find((t) => t.id === page.tab).route;
  return NAV.find((n) => n.id === page.nav).route;
}
function pageLabel(page) {
  if (page.label_no) return page.label_no;
  if (page.tab) return MEMBER_TABS.find((t) => t.id === page.tab).label_no;
  return NAV.find((n) => n.id === page.nav).label_no;
}

/**
 * The sections this person sees, each with the pages they may open: `{ id, label_no, pages: [{ key, nav, tab, label_no,
 * route }] }`. `can` is the person's permission check; `caps` the mosque's capabilities (null while loading = none).
 */
function sectionsFor(can, caps = null) {
  const navOk = new Set(NAV.filter((n) => permits(n, can) && capabilityAllows(n, caps)).map((n) => n.id));
  const tabsOk = new Set(memberTabsFor(can).map((t) => t.id));
  const out = [];
  for (const s of SECTIONS) {
    const pages = s.pages
      .filter((p) => navOk.has(p.nav) && (!p.tab || tabsOk.has(p.tab)))
      .map((p) => ({ key: p.tab ? p.nav + ':' + p.tab : p.nav, nav: p.nav, tab: p.tab || null, label_no: pageLabel(p), route: pageRoute(p) }));
    if (pages.length) out.push({ id: s.id, label_no: s.label_no, pages });
  }
  return out;
}

/**
 * Where an address belongs: `{ section, page }` among the person's own sections. A «Medlemmer» address is placed by its
 * tab (/medlemmer → Søknader, /medlemmer/register and /medlemmer/person/… → Medlemmer). Unknown → the first section.
 */
function sectionOf(route, can, caps = null) {
  const secs = sectionsFor(can, caps);
  if (!secs.length) return { section: null, page: null };
  const v = viewFor(route);
  const tab = v.id === 'members' ? memberRoute(v.route, can).tab : null;
  for (const s of secs) {
    const page = s.pages.find((p) => p.nav === v.id && (!p.tab || p.tab === tab));
    if (page) return { section: s, page };
  }
  return { section: secs[0], page: secs[0].pages[0] };
}

/**
 * A section's badge: the work waiting for this person in its pages, from badges() (which counts only what the viewer
 * may act on). «Søknader» carries the membership count; «Oppgaver» the open requests; «Arrangementer og innhold» the
 * janazah, announcements and banners waiting for a second person.
 */
function sectionBadges(b = {}) {
  const out = {};
  for (const s of SECTIONS) {
    let n = 0;
    for (const p of s.pages) {
      if (p.nav === 'members') { if (p.tab === 'inbox') n += b.members || 0; } else n += b[p.nav] || 0;
    }
    if (n) out[s.id] = n;
  }
  return out;
}

/**
 * Where a capability shows up in the panel (PM gaps B1/B9, 2026-11-01): the NAV item whose page it changes, or null for
 * one that only changes the member app (or is not built yet). «Funksjoner» groups its rows by the menu group of this
 * item and says «Vises under: <group> › <item>», and a switch's toast says where the item now lives. A NAV item GATED by
 * a capability (`capability` above) is its place; a test holds that every capability is listed here and agrees.
 */
const CAPABILITY_NAV = {
  prayer_times: 'times', events: 'events', notices: 'notices', janaza: 'notices', janaza_family_form: 'notices',
  screens: 'screens', screen_groups: 'screens', banners: 'banners', service_requests: 'inbox', library: null,
  donations: 'donations', push: 'notices', push_quiet_hours: 'notices', translations: 'trans',
  membership_consent: 'members', membership_confirmation: 'members', national_id: 'members', pid_matching: 'members', partner_scan: 'partners',
  event_rsvp: 'events', event_reminders: 'events', event_saved_sync: null, event_updates: 'events',
  youtube_live: 'screens', screen_remote: 'screens', youtube_live_auto: 'events', programme_runner: 'events',
  following: null, membership: 'members', membership_leave_in_app: null, store: 'store', store_checkout: 'store',
  tasks: 'work', checklists: 'checklists', planner: 'planner',
  event_registration: 'registration', event_capacity: 'registration', event_checkin: 'registration', ramadan_signup: 'ramadan',
  event_tickets: 'events', event_feedback: 'events', tasbih: 'tasbih', timetable_source: 'times', khutbah_plan: 'khutbah',
  social_publishing: 'notices', social_meta: 'notices', board_voting: 'voting', board_meetings: 'board', documents: 'documents',
  contacts_directory: 'contacts', contact_log: 'contacts', view_counts: 'notices', vipps_signatures: 'expenses', expenses: 'expenses',
  budget: 'budget', expenses_vipps_required: 'expenses', campaigns: 'campaigns', cash_gifts: 'cashGifts', recurring_gifts: 'donations',
  cash_gifts_vipps_required: 'cashGifts', board_minutes_vipps: 'board', member_polls: 'voting', whatsapp: 'whatsapp',
  family_relationships: null, birthday_notifications: null, community_analytics: null,
};

/**
 * capabilityPlace(key) → { nav, group, item_no, group_no, gates } | null. `gates`: the item is absent from the menu while
 * the capability is off (switching it on makes the item appear). null: the member app only.
 */
function capabilityPlace(key) {
  const id = Object.prototype.hasOwnProperty.call(CAPABILITY_NAV, key) ? CAPABILITY_NAV[key] : (NAV.find((n) => n.capability === key) || {}).id;
  const item = id ? NAV.find((n) => n.id === id) : null;
  if (!item) return null;
  const g = GROUPS.find((x) => x.id === (item.group || 'start'));
  return { nav: item.id, group: g ? g.id : 'start', item_no: item.label_no, group_no: (g && g.label_no) || null, gates: item.capability === key };
}

/** Addresses that moved. A bookmark to the old page lands on what replaced it. */
const ALIASES = { '/medlemsbekreftelse': '/medlemmer/import' };

/** The menu item an address belongs to: its own route, or a sub-address of it ('/medlemmer/register'). */
function viewFor(route) {
  const to = ALIASES[route] || route;
  const item = NAV.find((n) => n.route === to) || NAV.find((n) => n.route !== '/' && to.startsWith(n.route + '/'));
  return { id: item ? item.id : 'dash', route: to, moved: to !== route };
}

/**
 * The menu as it is DRAWN: the items this person may use, in core's order,
 * with a heading before the first item of each group.
 *
 * A heading with nothing under it is not drawn — an editor sees no
 * "Administrasjon" over an empty space, which would be the greyed-out menu
 * item that core rule 20 exists to forbid, just spelled differently.
 */
function navGroups(items = NAV) {
  const out = [];
  for (const g of GROUPS) {
    const rows = items.filter((n) => (n.group || 'start') === g.id);
    if (rows.length) out.push({ id: g.id, label_no: g.label_no, items: rows });
  }
  // A future item with no group, or a group id nobody defined, still appears:
  // a menu that silently drops a page is worse than an ungrouped one.
  const known = new Set(GROUPS.map((g) => g.id));
  const orphans = items.filter((n) => n.group && !known.has(n.group));
  if (orphans.length) out.push({ id: 'other', label_no: null, items: orphans });
  return out;
}

/**
 * The badge counts that appear next to a menu item.
 *
 * Only four things ever get a badge: unhandled requests, pushes waiting for
 * approval, membership work waiting on staff, and banners waiting for this person's approval. A badge on every item is wallpaper — the point of a badge is that
 * it means somebody has to do something.
 */
function badges(state = {}) {
  // Fault 58: the panel badged Forespørsler and OVERSETTELSER — a queue that
  // is never urgent — and nothing at all on a janazah waiting for a second
  // person, the one thing in the panel where minutes matter (CA-90's
  // recommendation is "keep the approval, make it fast"). The rule above was
  // right; the panel did something else.
  // 2026-10-15: and membership work waiting for this person (applications, joinings, withdrawals, reconciliations,
  // medlemsbevis that failed) — the third queue where a member waits on staff. Only what the viewer may act on.
  const out = {};
  if (state.openRequests) out.inbox = state.openRequests;
  if (state.membershipWork) out.members = state.membershipWork;
  // QA C (2026-10-16): a banner draft waiting for a second person (banners.approve) had no badge at all, so it waited
  // until somebody happened to open Bannere. Counted per viewer: only drafts this person may approve.
  if (state.bannersAwaitingApproval) out.banners = state.bannersAwaitingApproval;
  const waiting = (state.pushesAwaitingApproval || 0) + (state.noticesAwaitingApproval || 0);
  if (waiting) out.notices = waiting;
  return out;
}

/**
 * An item that belongs to a capability is shown only when the mosque HAS it (/v1/mosque `capabilities`). Unknown
 * (null, not loaded yet) is "no": the item appears once the list says so, never the other way round.
 */
function capabilityAllows(item, caps) {
  return !item.capability || (Array.isArray(caps) && caps.includes(item.capability));
}

/** Whether a nav item is for someone, given their `can(permission)`: its permission, or one of its `alsoFor` — and every one of its `alsoNeeds`. */
function permits(item, can) {
  return (!item.permission || can(item.permission) || (item.alsoFor || []).some(can)) && (item.alsoNeeds || []).every(can);
}

function navFor(role, state = {}, caps = null) {
  const b = badges(state);
  return NAV
    .filter(item => permits(item, (p) => access.can(role, p)) && capabilityAllows(item, caps))
    .map(item => ({ ...item, badge: b[item.id] || null }));
}

/**
 * Who gets into the panel.
 *
 * CORRECTED 2026-09-10. This said "staff sign-in reuses the member flow —
 * same phone number, same SMS code … no separate staff password". That stopped
 * being true with CA-17: staff sign in with e-mail and password
 * (`staff-auth.js`), and members now sign in with BankID or Vipps (CA-96). A
 * docstring describing a design that was replaced is how the next reader
 * builds against the old one.
 *
 * What still holds: the session is one day and non-refreshable — an admin
 * computer is shared, and a long admin session on it is the most likely way
 * this platform is compromised — and a member session is refused in words.
 */
function signIn(session, membership = null) {
  if (!session) return { ok: false, reason: 'not_signed_in', redirect: '/logg-inn' };
  const role = session.role || 'member';
  // Was `RANK[role] < RANK.staff`. There is no rank any more — that ladder is
  // what let an `approver` fall through every check written as "staff or
  // above". A role is either a staff role or it is not (CA-17).
  if (!access.isStaff(role)) {
    // A member who reaches the admin URL is not an attacker, usually. Say so
    // plainly rather than showing a 403 that looks like a fault.
    return { ok: false, reason: 'not_staff', role,
             message_no: 'Denne siden er for ansatte. Du er logget inn som medlem.' };
  }
  return {
    ok: true, role,
    nav: navFor(role),
    member: membership,
    // Every staff action is attributed. Shown in the header so nobody is in
    // any doubt whose name goes on an edit.
    actingAs: { id: session.member_id || null, phone: session.phone, role },
    sessionExpiresAt: session.expires_at,
  };
}

/**
 * Warn before the session dies mid-edit rather than after.
 *
 * A one-day staff session will expire while somebody is halfway through a
 * timetable upload. Losing that silently is how a person stops trusting the
 * tool.
 */
const WARN_MINUTES_BEFORE_EXPIRY = 20;
function sessionWarning(session, now = new Date()) {
  if (!session || !session.expires_at) return null;
  const minsLeft = (new Date(session.expires_at) - now) / 60000;
  if (minsLeft <= 0) return { state: 'expired', message_no: 'Økten er utløpt. Logg inn på nytt.' };
  if (minsLeft <= WARN_MINUTES_BEFORE_EXPIRY) {
    return { state: 'expiring', minutesLeft: Math.ceil(minsLeft),
             message_no: `Du blir logget ut om ${Math.ceil(minsLeft)} minutter. Lagre arbeidet ditt.` };
  }
  return null;
}

/**
 * What the shell shows across the top. Deliberately three things: who you
 * are, what needs attention, and whether anything is broken.
 */
function header(session, state = {}, now = new Date()) {
  return {
    actingAs: session ? { phone: session.phone, role: session.role } : null,
    warning: sessionWarning(session, now),
    attention: Object.values(badges(state)).reduce((a, b) => a + b, 0),
    // Surfaced from dashboard.js so a problem is visible on every page, not
    // only on the page somebody happens to open.
    systemAlert: state.criticalAlert || null,
  };
}

/**
 * Unsaved-changes guard. Not a nicety: the timetable editor and the janazah
 * composer are both long forms, and both are used under time pressure.
 */
function canLeave(dirty, target) {
  if (!dirty) return { ok: true };
  return {
    ok: false, target,
    message_no: 'Du har ulagrede endringer. Vil du forlate siden?',
    confirmLabel_no: 'Forlat siden', cancelLabel_no: 'Bli her',
  };
}

/** Breadcrumb for the mobile layout, where the sidebar is collapsed. */
function breadcrumb(route) {
  const item = NAV.find(n => n.route !== '/' && (route === n.route || route.startsWith(n.route + '/')));
  if (!item) return [{ label_no: 'Oversikt', route: '/' }];
  if (route === item.route) return [{ label_no: item.label_no, route: item.route }];
  return [{ label_no: item.label_no, route: item.route }, { label_no: 'Detaljer', route }];
}

module.exports = {
  NAV, GROUPS, SECTIONS, MEMBER_TABS, ALIASES, WARN_MINUTES_BEFORE_EXPIRY, CAPABILITY_NAV, capabilityPlace,
  sectionsFor, sectionOf, sectionBadges, memberTabsFor, memberRoute, viewFor, badges, navFor, navGroups, capabilityAllows, permits, signIn, sessionWarning, header, canLeave, breadcrumb,
};
